import { createRef, Component, Fragment, lazy, Suspense } from "react";
import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";

import Meta from "../meta";
import ModalContactListing from "../modal-contact-listing";
import ModalPdfs from "../modal-pdfs";

let openContactInit = false;
let openPdfsInit = false;

class Compare extends Component {
  constructor(props) {
    super(props);

    this.getCompareComponent = this.getCompareComponent.bind(this);
    this.formatTrackingEventLabel = this.formatTrackingEventLabel.bind(this);
    this.handleContact = this.handleContact.bind(this);
    this.handlePdfs = this.handlePdfs.bind(this);
    this.removeListing = this.removeListing.bind(this);
    this.trackCompared = this.trackCompared.bind(this);

    this.CompareComponents = {};

    this.contactButton = createRef();
    this.pdfsButton = createRef();

    this.state = { openContact: openContactInit, openPdfs: openPdfsInit };
  }

  /**
   * Get the correct drawer component for the target device
   * * Uses code React.lazy for code splitting
   * @public
   */
  getCompareComponent() {
    const {
      contactButton,
      CompareComponents,
      handleContact,
      handlePdfs,
      pdfsButton,
      removeListing,
    } = this;
    const { targetDevice, removeFromComparedListings, ...rest } = this.props;

    if (!!targetDevice) {
      if (!CompareComponents[targetDevice]) {
        CompareComponents[targetDevice] = lazy(
          () => import(`./components/Compare.${targetDevice}`)
        );
      }

      const CompareComponent = CompareComponents[targetDevice];
      const compareProps = {
        ...rest,
        contactButton: contactButton,
        handleContact: handleContact,
        handlePdfs: handlePdfs,
        pdfsButton: pdfsButton,
        remove: removeListing,
        targetDevice: targetDevice,
      };

      return <CompareComponent {...compareProps} />;
    } else {
      return null;
    }
  }

  /**
   * Format tracking event label
   * @param {object} listing
   * @public
   */
  formatTrackingEventLabel(listing) {
    let labelParts = [];

    if (!!listing.year) {
      labelParts.push(listing.year);
    }

    if (!!listing.make) {
      labelParts.push(listing.make);
    }

    if (!!listing.make && !!listing.model) {
      labelParts.push("–");
    }

    if (!!listing.model) {
      labelParts.push(listing.model);
    }

    if (!!listing.equipmentId) {
      labelParts.push(`(${listing.equipmentId})`);
    }

    return labelParts.length > 0 ? labelParts.join(" ") : "listing";
  }

  /**
   * Handle interactions with contact modal
   * @public
   */
  handleContact() {
    this.setState(
      ({ openContact }) => ({ openContact: !openContact }),
      () => {
        if (!this.state.openContact && this.contactButton.current) {
          this.contactButton.current.focus();
        }
      }
    );
  }

  /**
   * Handle interactions with contact modal
   * @public
   */
  handlePdfs() {
    this.setState(
      ({ openPdfs }) => ({ openPdfs: !openPdfs }),
      () => {
        if (!this.state.openPdfs && this.pdfsButton.current) {
          this.pdfsButton.current.focus();
        }
      }
    );
  }

  /**
   * Remove Listing
   * @param {object} listing
   */
  removeListing(listing) {
    this.props.removeFromComparedListings(listing.equipmentId);

    if (this.props.trackEvent) {
      this.props.trackEvent({
        category: "Compare",
        action: "Remove",
        label: this.formatTrackingEventLabel(listing),
      });
    }
  }

  /**
   * Track compared listings
   * @public
   */
  trackCompared() {
    if (this.props.trackEvent) {
      const { addDataLayerListingParameters, comparedListings: listings } =
        this.props;

      addDataLayerListingParameters(listings).then(() => {
        this.props.trackEvent({
          category: "Compare",
          action: "View",
          label: listings
            .map(listing => this.formatTrackingEventLabel(listing))
            .join(", "),
          value: listings.length,
        });
      });
    }
  }

  componentDidMount() {
    this.trackCompared();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.comparedListings.length !== this.props.comparedListings.length
    ) {
      this.trackCompared();
    }
  }

  componentWillUnmount() {
    openContactInit = this.state.openContact;
    openPdfsInit = this.state.openPdfs;
  }

  render() {
    const { getCompareComponent, handleContact, handlePdfs } = this;
    const {
      authenticated,
      comparedListings: listings,
      targetDevice,
      trackEvent,
      trackModalView,
    } = this.props;
    const { openContact, openPdfs } = this.state;

    return (
      <Fragment>
        <Meta title="Compare Listings" />
        <Suspense fallback={null}>{getCompareComponent()}</Suspense>
        {!authenticated && (
          <ModalContactListing
            analyticsLabel="compare"
            close={handleContact}
            listings={listings}
            open={openContact}
            targetDevice={targetDevice}
            trackEvent={trackEvent}
            trackModalView={trackModalView}
          />
        )}
        <ModalPdfs
          analyticsLabel="compare"
          close={handlePdfs}
          listings={listings}
          open={openPdfs}
          targetDevice={targetDevice}
          trackEvent={trackEvent}
          trackModalView={trackModalView}
        />
      </Fragment>
    );
  }
}

Compare.propTypes = {
  addDataLayerListingParameters: func.isRequired,
  authenticated: bool,
  comparedListings: arrayOf(
    shape({
      equipmentId: oneOfType([number, string]),
    })
  ),
  removeFromComparedListings: func.isRequired,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
};

export default Compare;
